import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { useCurrentUser } from './CurrentUserContext';

const ExpensesContext = createContext(null);

export const useExpenses = () => {
  const context = useContext(ExpensesContext);
  if (!context) throw new Error('useExpenses must be used within an ExpensesProvider');
  return context;
};

// Normalize API response (camelCase) to frontend shape (snake_case for legacy compat)
function normalizeExpense(exp) {
  return {
    id: Number(exp.id),
    title: exp.title,
    amount: Number(exp.amount),
    date: exp.date,
    category: exp.category || 'Other',
    project_id: exp.projectId ?? null,
    contact_id: exp.contactId ?? null,
    receipt_url: exp.receiptUrl ?? null,
    notes: exp.notes ?? '',
    is_billable: exp.isBillable ?? false,
    is_billed: exp.isBilled ?? false,
    tax_rate: exp.taxRate ?? 0,
    created_at: exp.createdAt,
    // TeamBooks P2-3: approval workflow
    user_id: exp.userId ?? null,
    approval_status: exp.approvalStatus ?? 'approved',
    rejection_reason: exp.rejectionReason ?? null,
  };
}

export const ExpensesProvider = ({ children }) => {
  const { currentUser, activeWorkspaceId } = useCurrentUser();
  const [expenses, setExpenses] = useState([]);

  const refresh = useCallback(() => {
    api('/api/expenses')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.expenses)) setExpenses(data.expenses.map(normalizeExpense));
      })
      .catch(err => console.error('Failed to load expenses:', err));
  }, []);

  // Refetch on workspace switch too — this provider wraps the AppContent remount
  // boundary, so it must key off activeWorkspaceId (mirrors ContactsContext).
  useEffect(() => {
    if (!currentUser?.id) return;
    refresh();
  }, [currentUser?.id, activeWorkspaceId, refresh]);

  useEffect(() => {
    window.addEventListener('aidey:expensesUpdated', refresh);
    return () => window.removeEventListener('aidey:expensesUpdated', refresh);
  }, [refresh]);

  return (
    <ExpensesContext.Provider value={{ expenses, setExpenses, refresh }}>
      {children}
    </ExpensesContext.Provider>
  );
};
